@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE", "CAST_NEVER_SUCCEEDS")

package org.example.project.enginetabular

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.settings.UISettings
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event

@Composable
fun TabularTable(
    visibleColumns: List<String>,
    headerDisplayLabels: Map<String, String> = emptyMap(),
    renderedData: List<Pair<String, List<String>>>,
    headerNameToIndex: Map<String, Int>,
    nonEditableColIndices: Set<Int>,
    toggleColIndices: Set<Int>,
    dropdownOptionIndices: Map<Int, List<String>>,
    rowDropdownOptions: ((rowId: String, row: List<String>, colIndex: Int) -> List<String>?)? = null,
    calendarColumnIndices: Set<Int> = emptySet(),
    multilineColumnIndices: Set<Int> = emptySet(),
    cellBackgroundColors: Map<Pair<String, Int>, CellColor>,
    editable: Boolean,
    selectionMode: String = TabularSelectionMode.ROWS,
    selectedRows: Set<String>,
    selectedRowIndices: Set<Int> = emptySet(),
    selectedCell: Pair<String, Int>? = null,
    selectedCellRange: TabularCellRange? = null,
    filterTexts: List<MutableState<String>>,
    hoveredColIndex: Int?,
    onColumnOrderChange: (String, String) -> Unit,
    onHoverColumnChange: (Int?) -> Unit,
    onHeaderClick: ((Int) -> Unit)?,
    onHeaderLabelEdit: ((Int, String) -> Unit)? = null,
    onRowSelect: (id: String, index: Int, isShift: Boolean, isCtrl: Boolean) -> Unit,
    onRowClick: ((String) -> Unit)?,
    onRowDoubleClick: ((String) -> Unit)? = null,
    onRowIndexSelect: ((Int, Boolean, Boolean) -> Unit)? = null,
    onRowIndexContextMenu: ((Int, Double, Double) -> Unit)? = null,
    onCellClick: ((String, Int) -> Unit)?,
    onCellSelectionChange: ((TabularCellRange) -> Unit)? = null,
    onCellContextMenu: ((String, Int, Double, Double) -> Unit)? = null,
    onCellEdit: ((String, Int, String) -> Unit)?,
    sortColumn: String? = null,
    sortAscending: Boolean = true,
    onSortChange: ((String) -> Unit)? = null,
    showFillerRows: Boolean = true,
    showFilterRow: Boolean = true,
    headerHeightCss: String? = null,
    rowHeightCss: String? = null,
    cellFontSizeCss: String? = null,
    indexColumnWidthCss: String? = null,
    headerFontSizeCss: String? = null,
    dividerColorCss: String? = null,
    rowLabelProvider: ((String, Int) -> String)? = null,
    dataColumnWidthCss: String? = null,
    selectionOutlineOnly: Boolean = false,
    suppressRowHighlight: Boolean = false,
    suppressIndexHighlight: Boolean = false,
    smartMode: Boolean = false,
    accentHeaders: Set<String> = emptySet(),
    onCellHoverEnter: ((String, Int) -> Unit)? = null,
    onCellHoverLeave: ((String, Int) -> Unit)? = null,
    onCellHoverMove: ((String, Int, Double, Double) -> Unit)? = null,
    onCellClickPosition: ((String, Int, Double, Double) -> Unit)? = null,
    cellEditable: (rowId: String, column: String, value: String) -> Boolean = { _, _, _ -> true }
) {
    var viewportRows by remember { mutableStateOf((((window.innerHeight - 260) / 28.0).toInt()).coerceAtLeast(8)) }
    var isDraggingSelection by remember { mutableStateOf(false) }
    var dragAnchor by remember { mutableStateOf<TabularCellRange?>(null) }

    DisposableEffect(Unit) {
        val onResize: (Event) -> Unit = {
            viewportRows = (((window.innerHeight - 260) / 28.0).toInt()).coerceAtLeast(8)
        }
        window.addEventListener("resize", onResize)
        onDispose { window.removeEventListener("resize", onResize) }
    }

    fun formatToGerman(isoDate: String): String {
        return if (isoDate.contains("-") && isoDate.length == 10) {
            val p = isoDate.split("-")
            if (p.size == 3) "${p[2]}.${p[1]}.${p[0]}" else isoDate
        } else isoDate
    }

    fun formatToIso(germanDate: String): String {
        return if (germanDate.contains(".") && germanDate.length == 10) {
            val p = germanDate.split(".")
            if (p.size == 3) "${p[2]}-${p[1]}-${p[0]}" else germanDate
        } else germanDate
    }

    Div {
        Table({ classes(TableStyles.table) }) {
            Colgroup {
                Col(attrs = {
                    style {
                        indexColumnWidthCss?.let {
                            property("width", it)
                            property("min-width", it)
                            property("max-width", it)
                        }
                    }
                })
                repeat(visibleColumns.size) {
                    Col(attrs = {
                        style {
                            dataColumnWidthCss?.let {
                                property("width", it)
                                property("min-width", it)
                                property("max-width", it)
                            }
                        }
                    })
                }
            }

            TabularHeader(
                visibleColumns = visibleColumns,
                headerDisplayLabels = headerDisplayLabels,
                accentHeaders = accentHeaders,
                headerNameToIndex = headerNameToIndex,
                hoveredColIndex = hoveredColIndex,
                filterTexts = filterTexts,
                onColumnOrderChange = onColumnOrderChange,
                onHoverColumnChange = onHoverColumnChange,
                onHeaderClick = onHeaderClick,
                onHeaderLabelEdit = onHeaderLabelEdit,
                sortColumn = sortColumn,
                sortAscending = sortAscending,
                onSortChange = onSortChange,
                showFilterRow = showFilterRow,
                headerHeightCss = headerHeightCss,
                filterRowTopCss = headerHeightCss,
                indexColumnWidthCss = indexColumnWidthCss,
                headerFontSizeCss = headerFontSizeCss,
                dividerColorCss = dividerColorCss,
                dataColumnWidthCss = dataColumnWidthCss,
                smartMode = smartMode
            )

            Tbody {
                val hoveredRowId = remember { mutableStateOf<String?>(null) }
                renderedData.forEachIndexed { index, (id, row) ->
                    key(id) {
                        val rowIsRangeSelected = selectionMode == TabularSelectionMode.CELLS &&
                            (
                                index in selectedRowIndices ||
                                    (
                                        selectedCellRange != null &&
                                            selectedCell == null &&
                                            selectedCellRange.topRow <= index &&
                                            selectedCellRange.bottomRow >= index &&
                                            selectedCellRange.leftCol == 0 &&
                                            selectedCellRange.rightCol >= visibleColumns.lastIndex.coerceAtLeast(0)
                                    )
                                )

                        Tr({
                            classes(TableStyles.tr, TableStyles.row)
                            if (smartMode) {
                                classes(TableStyles.smartRow)
                            }
                            onMouseOver { hoveredRowId.value = id }
                            onMouseOut { hoveredRowId.value = null }
                            onClick { ev ->
                                if (selectionMode == TabularSelectionMode.ROWS) {
                                    onRowSelect(id, index, ev.shiftKey, ev.ctrlKey || ev.metaKey)
                                    onRowClick?.invoke(id)
                                }
                            }
                            onDoubleClick {
                                if (selectionMode == TabularSelectionMode.ROWS) {
                                    onRowDoubleClick?.invoke(id)
                                } else if (selectionMode == TabularSelectionMode.CELLS) {
                                    val firstVisibleCol = visibleColumns.firstOrNull()?.let { headerNameToIndex[it] } ?: 0
                                    onCellSelectionChange?.invoke(
                                        TabularCellRange(index, firstVisibleCol, index, visibleColumns.lastIndex.coerceAtLeast(0))
                                    )
                                }
                            }
                        }) {
                            Td({
                                classes(TableStyles.td, TableStyles.scrollSnapCell)
                                if (smartMode) {
                                    classes(TableStyles.smartTd)
                                }
                                style {
                                    rowHeightCss?.let { property("height", it) }
                                    cellFontSizeCss?.let { property("font-size", it) }
                                    indexColumnWidthCss?.let {
                                        property("width", it)
                                        property("min-width", it)
                                        property("max-width", it)
                                    }
                                    dividerColorCss?.let {
                                        property("border-right", "1px solid $it")
                                        property("border-bottom", "1px solid $it")
                                    }
                                    if (!suppressIndexHighlight && (selectedRows.contains(id) || rowIsRangeSelected)) {
                                        backgroundColor(
                                            if (rowIsRangeSelected) UISettings.TABULAR_HOVER_BG else UISettings.TABULAR_SELECTED_BG
                                        )
                                        property("border-bottom", "1px solid transparent")
                                        property("border-right", "1px solid transparent")
                                    } else if (!suppressIndexHighlight && hoveredRowId.value == id) {
                                        backgroundColor(UISettings.TABULAR_HOVER_BG)
                                        property("border-bottom", "1px solid transparent")
                                        property("border-right", "1px solid transparent")
                                    }
                                }
                                onClick {
                                if (selectionMode == TabularSelectionMode.CELLS) {
                                        onRowIndexSelect?.invoke(index, it.shiftKey, it.ctrlKey || it.metaKey)
                                    }
                                }
                                onContextMenu {
                                    if (selectionMode == TabularSelectionMode.CELLS) {
                                        it.preventDefault()
                                        val mouseEvent = it.nativeEvent as? org.w3c.dom.events.MouseEvent
                                        onRowIndexContextMenu?.invoke(
                                            index,
                                            mouseEvent?.clientX?.toDouble() ?: 0.0,
                                            mouseEvent?.clientY?.toDouble() ?: 0.0
                                        )
                                    }
                                }
                            }) {
                                Text(rowLabelProvider?.invoke(id, index) ?: (index + 1).toString())
                            }

                            visibleColumns.forEach { header ->
                                val colIndex = headerNameToIndex[header] ?: return@forEach
                                val rawValue = row.getOrElse(colIndex) { "" }
                                val isDate = header.contains("Datum", true) || header.contains(" am", true)
                                val isInSelectedRange = selectedCellRange?.contains(index, colIndex) == true
                                val isSelectedCell = selectedCell == (id to colIndex)
                                val isSelectedArea = isSelectedCell || isInSelectedRange

                                val cellState = remember(id, colIndex, rawValue) {
                                    mutableStateOf(if (isDate) formatToGerman(rawValue) else rawValue)
                                }

                                EditableTableCell(
                                    id = id,
                                    colIndex = colIndex,
                                    cellValue = cellState.value,
                                    isEditable = editable && colIndex !in nonEditableColIndices && cellEditable(id, header, rawValue),
                                    isToggleCell = colIndex in toggleColIndices,
                                    dropdownOptions = rowDropdownOptions?.invoke(id, row, colIndex) ?: dropdownOptionIndices[colIndex],
                                    showCalendarPopup = colIndex in calendarColumnIndices,
                                    multiline = colIndex in multilineColumnIndices,
                                    backgroundColor = cellBackgroundColors[id to colIndex],
                                    onCellEdit = { rowId, col, newText ->
                                        val finalValue = if (isDate) formatToIso(newText) else newText
                                        cellState.value = if (isDate) formatToGerman(finalValue) else newText
                                        onCellEdit?.invoke(rowId, col, finalValue)
                                    },
                                    onCellClick = onCellClick,
                                    onCellMouseDown = { rowId, col ->
                                        if (selectionMode == TabularSelectionMode.CELLS) {
                                            isDraggingSelection = true
                                            val range = TabularCellRange(index, col, index, col)
                                            dragAnchor = range
                                            onCellSelectionChange?.invoke(range)
                                            onCellClick?.invoke(rowId, col)
                                        }
                                    },
                                    onCellMouseEnter = { _, col ->
                                        onCellHoverEnter?.invoke(id, col)
                                        if (selectionMode == TabularSelectionMode.CELLS && isDraggingSelection) {
                                            val anchor = dragAnchor ?: TabularCellRange(index, col, index, col)
                                            onCellSelectionChange?.invoke(
                                                anchor.copy(kapazitaetCcg1 = index, kapazitaetCcg2 = col)
                                            )
                                        }
                                    },
                                    onCellMouseLeave = { _, col ->
                                        onCellHoverLeave?.invoke(id, col)
                                    },
                                    onCellMouseMove = { _, col, clientX, clientY ->
                                        onCellHoverMove?.invoke(id, col, clientX, clientY)
                                    },
                                    onCellClickPosition = { _, col, clientX, clientY ->
                                        onCellClickPosition?.invoke(id, col, clientX, clientY)
                                    },
                                    onCellMouseUp = { _, col ->
                                        if (selectionMode == TabularSelectionMode.CELLS) {
                                            val anchor = dragAnchor ?: TabularCellRange(index, col, index, col)
                                            onCellSelectionChange?.invoke(
                                                anchor.copy(kapazitaetCcg1 = index, kapazitaetCcg2 = col)
                                            )
                                            isDraggingSelection = false
                                        }
                                    },
                                    onCellContextMenu = onCellContextMenu,
                                    isHoveredRow = hoveredRowId.value == id,
                                    isSelectedRow = selectionMode == TabularSelectionMode.ROWS && selectedRows.contains(id),
                                    isRowRangeSelected = rowIsRangeSelected,
                                    isSelectedCell = isSelectedCell,
                                    isInSelectedRange = isInSelectedRange,
                                    isRangeTopEdge = isSelectedArea && !rowIsRangeSelected && selectedCellRange?.topRow == index,
                                    isRangeBottomEdge = isSelectedArea && !rowIsRangeSelected && selectedCellRange?.bottomRow == index,
                                    isRangeLeftEdge = isSelectedArea && !rowIsRangeSelected && selectedCellRange?.leftCol == colIndex,
                                    isRangeRightEdge = isSelectedArea && !rowIsRangeSelected && selectedCellRange?.rightCol == colIndex,
                                    startEditingOnSingleClick = selectionMode == TabularSelectionMode.ROWS,
                                    rowHeightCss = rowHeightCss,
                                    cellFontSizeCss = cellFontSizeCss,
                                    dividerColorCss = dividerColorCss,
                                    dataColumnWidthCss = dataColumnWidthCss,
                                    selectionOutlineOnly = selectionOutlineOnly,
                                    suppressRowHighlight = suppressRowHighlight,
                                    smartMode = smartMode
                                )
                            }
                        }
                    }
                }

                if (showFillerRows) {
                    val fillerRows = (viewportRows - renderedData.size).coerceAtLeast(0)
                    repeat(fillerRows) {
                        Tr({
                            classes(TableStyles.tr, TableStyles.row)
                            if (smartMode) {
                                classes(TableStyles.smartRow)
                            }
                        }) {
                            Td({
                                classes(TableStyles.td, TableStyles.scrollSnapCell)
                                if (smartMode) {
                                    classes(TableStyles.smartTd)
                                }
                            }) { Text("") }
                            repeat(visibleColumns.size) {
                                Td({
                                    classes(TableStyles.td, TableStyles.scrollSnapCell)
                                    if (smartMode) {
                                        classes(TableStyles.smartTd)
                                    }
                                    style {
                                        rowHeightCss?.let { property("height", it) }
                                        cellFontSizeCss?.let { property("font-size", it) }
                                    }
                                }) { Text("") }
                            }
                        }
                    }
                }
            }
        }
    }
}
